import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authcontext';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../services/firebase';
import { useNavigate, useLocation } from 'react-router-dom';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');
    if (message) {
      setSuccessMessage(message);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setResetEmailSent(false);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid Password');
      } else {
        setError(err.message);
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      alert('Please enter your email address first');
      return;
    }
    
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
      setError('');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else {
        setError('Failed to send password reset email: ' + err.message);
      }
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #1e1e2f 0%, #2d3748 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        fontFamily: 'Poppins, sans-serif',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;700;900&display=swap');
        @keyframes fadeInSlide {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .loginCard {
          background-color: rgba(45, 55, 72, 0.9);
          padding: 2.5rem 2rem 2rem 2rem;
          border-radius: 16px;
          box-shadow: 0 6px 24px rgba(0,0,0,0.35);
          width: 100%;
          max-width: 500px;
          backdrop-filter: blur(5px);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .logoAnimated {
          width: 180px;
          max-width: 80%;
          margin-bottom: 1.2rem;
          filter: drop-shadow(0 0 16px #4A90E2) drop-shadow(0 2px 8px #0008);
          animation: fadeInSlide 0.8s cubic-bezier(.4,1.4,.6,1) 0.1s both;
          display: block;
        }
        .zenTextLogo {
          font-family: 'Poppins', sans-serif;
          color: #e0e0e0;
          font-size: 2.1rem;
          font-weight: 900;
          letter-spacing: 1px;
          text-align: center;
          margin-bottom: 2.2rem;
          text-shadow: 0 2px 12px #4A90E2, 0 1px 4px #0008;
          animation: fadeInSlide 0.8s cubic-bezier(.4,1.4,.6,1) 0.2s both;
        }
        .inputWrapper {
          position: relative;
          margin-bottom: 1.5rem;
          width: 100%;
        }
        .inputWrapper input {
          width: 100%;
          padding: 12px 12px 12px 12px;
          background-color: #4a5568;
          color: #e0e0e0;
          border: 1px solid #4a5568;
          border-radius: 4px;
          font-family: Poppins, sans-serif;
          font-size: 15px;
          transition: border-color 0.3s;
        }
        .inputWrapper input:focus {
          border-color: #4A90E2;
          outline: none;
        }
        .inputWrapper label {
          position: absolute;
          top: 12px;
          left: 14px;
          color: #a0aec0;
          font-size: 15px;
          transition: all 0.3s;
          pointer-events: none;
          background: transparent;
        }
        .inputWrapper input:focus + label,
        .inputWrapper input:not(:placeholder-shown) + label {
          top: -12px;
          left: 8px;
          font-size: 12px;
          color: #4A90E2;
          background-color: #2d3748;
          padding: 0 6px;
        }
        .loginButton {
          width: 100%;
          padding: 12px;
          background: linear-gradient(90deg, #4A90E2 0%, #357ABD 100%);
          color: #e0e0e0;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-family: Poppins, sans-serif;
          font-size: 15px;
          font-weight: 700;
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          box-shadow: 0 2px 8px #0004;
          transition: transform 0.1s, background 0.3s;
        }
        .loginButton:hover {
          transform: scale(1.03);
          background: linear-gradient(90deg, #5BA3F5 0%, #468CD0 100%);
        }
        .logoutButton {
          width: 100%;
          padding: 10px;
          background: transparent;
          color: #4A90E2;
          border: none;
          cursor: pointer;
          font-family: Poppins, sans-serif;
          font-size: 14px;
          text-align: center;
          margin-top: 0.5rem;
        }
        .errorMsg {
          color: #ff5555;
          text-align: center;
          margin-bottom: 1rem;
          font-size: 15px;
        }
      `}</style>
      <div className="loginCard">
        <img src="/zenbuildlogo.png" alt="ZenBidTracker Logo" className="logoAnimated" />
        <div className="zenTextLogo">ZenBidTracker</div>
                    {error && <div className="errorMsg">{error}</div>}
            {successMessage && (
              <div style={{
                background: '#4caf50',
                color: 'white',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '15px',
                textAlign: 'center',
                fontSize: '14px'
              }}>
                {successMessage}
              </div>
            )}
            {resetEmailSent && (
              <div style={{
                background: '#4caf50',
                color: 'white',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '15px',
                textAlign: 'center',
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                <strong>Password reset email sent!</strong><br />
                Check your email ({email}) for a link to reset your password.<br />
                <br />
                <span style={{ fontSize: '13px', opacity: '0.9' }}>
                  💡 If you don't see the email in your inbox, please check your spam/junk folder
                </span>
              </div>
            )}
        <form onSubmit={handleLogin} style={{ width: '100%' }}>
          <div className="inputWrapper">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder=" "
              required
              autoComplete="username"
            />
            <label htmlFor="email">Email</label>
          </div>
          <div className="inputWrapper">
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder=" "
              required
              autoComplete="current-password"
            />
            <label htmlFor="password">Password</label>
          </div>
          {error === 'Invalid Password' && (
            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
              <button 
                type="button"
                onClick={handleForgotPassword}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#4A90E2', 
                  fontSize: '14px', 
                  textDecoration: 'underline', 
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          <button type="submit" className="loginButton">Log In</button>
        </form>
        <button
          type="button"
          onClick={() => navigate('/signup')}
          style={{
            width: '100%',
            padding: '10px',
            background: 'transparent',
            color: '#4A90E2',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            textAlign: 'center',
            marginTop: '1rem'
          }}
        >
          Need an account? Sign Up
        </button>
      </div>
    </div>
  );
}

export default LoginPage; 